import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';

export const useAuth = () => {
    const { session, user, loading, signOut } = useAuthContext();
    const [authLoading, setAuthLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const signUp = async (email: string, password: string, username: string) => {
        setAuthLoading(true);
        setError(null);
        try {
            const { data, error: signUpError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        username: username
                    },
                    emailRedirectTo: `${window.location.origin}/dashboard`
                }
            });

            if (signUpError) {
                // If the user already exists, we guide them to the login page.
                if (signUpError.message.toLowerCase().includes('already registered')) {
                    return {
                        success: false,
                        error: 'Email already registered. Please sign in instead.',
                        isExistingUser: true
                    };
                }
                throw signUpError;
            }

            // Detect masked duplicate signup (Supabase security feature)
            // If user exists, it returns a user object but with empty identities
            if (data.user && data.user.identities && data.user.identities.length === 0) {
                return {
                    success: false,
                    error: 'Email already registered. Please sign in instead.',
                    isExistingUser: true
                };
            }

            return { success: true, session: data.session };
        } catch (err: unknown) {
            console.error('Signup error:', err);
            const message = err instanceof Error ? err.message : 'Sign up failed';
            setError(message);
            return { success: false, error: message };
        } finally {
            setAuthLoading(false);
        }
    };

    const signIn = async (email: string, password: string) => {
        setAuthLoading(true);
        setError(null);
        try {
            const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
            if (signInError) throw signInError;
            return { success: true };
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Sign in failed';
            setError(message);
            return { success: false, error: message };
        } finally {
            setAuthLoading(false);
        }
    };

    const signInWithGoogle = async () => {
        setAuthLoading(true);
        setError(null);
        try {
            const { error: oauthError } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: { redirectTo: `${process.env.REACT_APP_APP_URL}/dashboard` },
            });
            if (oauthError) throw oauthError;
            return { success: true };
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Google sign in failed';
            setError(message);
            return { success: false, error: message };
        } finally {
            setAuthLoading(false);
        }
    };

    return { session, user, loading, authLoading, error, signUp, signIn, signInWithGoogle, signOut };
};
